import { Alert, Stack } from "@mui/material";
import type { BusinessInformationStepSchema } from "Components/Onboarding/BusinessEntity/Steps/BusinessInformationStep/BusinessInformationStepContent";
import { StyledValidationText } from "Components/Shared/FormStyles";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { VatRegistryStatusText } from "Components/Shared/Vat/VatRegistryStatusText";
import type { VatStatus } from "Infrastructure/Api/Api";
import { normalizeVatNumber } from "Schemas/vatNumberSchema";
import { Resources, useResource } from "Translations/Resources";
import { type Control, useFormState } from "react-hook-form";

const COMPONENT_RESOURCES =
  Resources.Onboarding.BusinessOnboarding.Steps.BusinessInformation;

type Props = {
  control: Control<BusinessInformationStepSchema>;
  vatNumber: string;
  vatRegistryStatus: VatStatus;
  // The register gave no word (outage, or no DIČ to derive the number from) and the user is not editing.
  isVatRegistryHintShown: boolean;
  // An SK company ending without an IČ DPH gets Czech VAT on its invoices — it should know why (§ 7a).
  isSlovakVatNoticeShown: boolean;
  onChange: () => void;
  onFocus: () => void;
  onBlur: () => void;
};

/**
 * The IČ DPH input of the business-information step with the register's word under it. Only for a market
 * whose business register does not publish the number (SK) — in CZ the number is the DIČ and stays hidden.
 */
export const VatNumberField = ({
  control,
  vatNumber,
  vatRegistryStatus,
  isVatRegistryHintShown,
  isSlovakVatNoticeShown,
  onChange,
  onFocus,
  onBlur,
}: Props) => {
  const { t } = useResource();
  // Rendered by the group's own error slot below, not by the input's helper line — see the JSX.
  const { errors } = useFormState({ control, name: "vatNumber" });

  return (
    // The number and the register's word on it are one question, so they form a single child of the
    // form's grid — otherwise each sits a full field-gap below. They also share one error slot under
    // the group instead of the helper line the input reserves by default; the slot is always rendered
    // (min-height 20px), so nothing shifts when the message appears. Same shape Dropdown uses.
    <Stack gap={1}>
      <FormInput
        disableHelperText
        control={control}
        name="vatNumber"
        label={t(Resources.Common.VatNumber)}
        valueTransformer={normalizeVatNumber}
        onAfterChange={onChange}
        onFocus={onFocus}
        onAfterBlur={onBlur}
      />
      <VatRegistryStatusText
        vatRegistryStatus={vatRegistryStatus}
        vatNumber={vatNumber}
        openQuestionText={
          isVatRegistryHintShown
            ? t(Resources.Common.VatRegistryUnavailableHint)
            : null
        }
      />
      <StyledValidationText>{errors.vatNumber?.message}</StyledValidationText>
      {isSlovakVatNoticeShown && (
        <Alert severity="info">
          {t(COMPONENT_RESOURCES.SlovakVatRegistrationNotice)}
        </Alert>
      )}
    </Stack>
  );
};
